// BILLING-PLANS (§6.2, phase 3): Operator › Billing › Invoices & quotes — one table across the
// granted companies with filter chips (All · Draft · Open · Overdue · Paid · Void · Quotes);
// hover Send, Remind, Mark paid (modal: date, reference), Void (confirmed), PDF ↗ on invoices,
// and Send, Cancel (confirmed), View ↗ on quotes. "New" opens one modal for an invoice or a
// quote: company, lines (catalog, custom or a project milestone), net terms, expiry, memo;
// Save draft or Send.
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BanIcon,
  BellRingIcon,
  CircleCheckIcon,
  FileDownIcon,
  PlusIcon,
  ReceiptIcon,
  SendIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import type { Api } from "../../../api/client";
import { ONE_TIME_CHARGES } from "../../billing/flags";
import { centsToDollarsText, parseDollarsToCents } from "../../billing/money";
import {
  InvoiceStatusChip,
  QuoteStatusChip,
  RowLinkOut,
  SOURCE_LABEL,
} from "../../billing/plan/parts";
import {
  type OperatorInvoiceRow,
  type OperatorQuoteRow,
  opBillingKeys,
  opCompaniesQuery,
  opCompanyQuery,
  opInvoicesQuery,
  priceListsQuery,
} from "../../billing/plan-queries";
import { useQuotePdf } from "../../billing/quote-pdf";
import { formatDate, formatMoney } from "../../format";
import { LoadError, Loading, LoadMore } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import { addDaysIso, FormDialog, SelectField, TextField, todayIso } from "./form-parts";

export const INVOICE_FILTERS = [
  "all",
  "draft",
  "open",
  "overdue",
  "paid",
  "uncollectible",
  "void",
  "quotes",
] as const;
export type InvoiceFilter = (typeof INVOICE_FILTERS)[number];
const FILTER_LABEL: Record<InvoiceFilter, string> = {
  all: "All",
  draft: "Draft",
  open: "Open",
  overdue: "Overdue",
  paid: "Paid",
  uncollectible: "Uncollectible",
  void: "Void",
  quotes: "Quotes",
};

function useRefresh() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: opBillingKeys.all() }),
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "company" && q.queryKey[2] === "billing",
      }),
    ]);
}

export function InvoicesTab({ api }: { api: Api }) {
  const [filter, setFilter] = useState<InvoiceFilter>("all");
  const [companyFilter, setCompanyFilter] = useState("");
  const companyList = useQuery(opCompaniesQuery(api));
  const q = useInfiniteQuery(
    opInvoicesQuery(
      api,
      filter === "all" || filter === "quotes" ? undefined : filter,
      companyFilter || undefined,
    ),
  );
  const [paying, setPaying] = useState<OperatorInvoiceRow | null>(null);
  const [voiding, setVoiding] = useState<OperatorInvoiceRow | null>(null);
  const [cancelling, setCancelling] = useState<OperatorQuoteRow | null>(null);
  const refresh = useRefresh();
  const params = (r: { company_id: string; id: string }) => ({
    company_id: r.company_id,
    invoice_id: r.id,
  });
  const send = useMutation({
    mutationFn: (r: OperatorInvoiceRow) =>
      api.client.call("operator.billing.invoices.send", { params: params(r) }),
    onSuccess: (r) => toast.add({ title: `Invoice ${r.number ?? ""} sent`, type: "success" }),
    onSettled: refresh,
  });
  const remind = useMutation({
    mutationFn: (r: OperatorInvoiceRow) =>
      api.client.call("operator.billing.invoices.remind", { params: params(r) }),
    onSuccess: (r) => toast.add({ title: `Reminder sent for ${r.number ?? "the invoice"}` }),
    onSettled: refresh,
  });
  const voidIt = useMutation({
    mutationFn: (r: OperatorInvoiceRow) =>
      api.client.call("operator.billing.invoices.void", { params: params(r) }),
    onSuccess: () => setVoiding(null),
    onSettled: refresh,
  });
  const quoteParams = (r: OperatorQuoteRow) => ({ company_id: r.company_id, quote_id: r.id });
  const pdf = useQuotePdf(api);
  const sendQuote = useMutation({
    mutationFn: (r: OperatorQuoteRow) =>
      api.client.call("operator.billing.quotes.send", { params: quoteParams(r) }),
    onSuccess: (r) => toast.add({ title: `Quote ${r.number ?? ""} sent`, type: "success" }),
    onSettled: refresh,
  });
  const cancelQuote = useMutation({
    mutationFn: (r: OperatorQuoteRow) =>
      api.client.call("operator.billing.quotes.cancel", { params: quoteParams(r) }),
    onSuccess: () => setCancelling(null),
    onSettled: refresh,
  });

  if (q.isPending) return <Loading label="Loading invoices" rows={4} />;
  if (q.isError) return <LoadError what="Invoices" error={q.error} />;
  const first = q.data.pages[0];
  const allInvoices = q.data.pages.flatMap((p) => p.invoices);
  const quotes = first?.quotes ?? [];
  const skipped = first?.skipped ?? 0;
  const invoices =
    filter === "all" ? allInvoices : allInvoices.filter((i) => i.display_status === filter);
  return (
    <div className="flex flex-col gap-3" data-slot="billing-invoices-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          aria-label="Show"
          size="sm"
          value={filter}
          onValueChange={setFilter}
          options={INVOICE_FILTERS.filter((f) => ONE_TIME_CHARGES || f !== "quotes").map((f) => ({
            value: f,
            label: FILTER_LABEL[f],
          }))}
        />
        <NativeSelect
          aria-label="Company"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
        >
          <NativeSelectOption value="">All companies</NativeSelectOption>
          {(companyList.data?.data ?? []).map((c) => (
            <NativeSelectOption key={c.company_id} value={c.company_id}>
              {c.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      {skipped > 0 ? (
        <p className="text-[13px] text-warning-text">
          {skipped} compan{skipped === 1 ? "y" : "ies"} could not be read; reload.
        </p>
      ) : null}
      {filter === "quotes" ? (
        quotes.length === 0 ? (
          <EmptyState variant="card" icon={ReceiptIcon} title="No quotes yet" />
        ) : (
          <Table className={DENSE_TABLE} data-slot="op-quotes">
            <TableCaption className="sr-only">Quotes across companies.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Company</TableHead>
                <TableHead scope="col">Number</TableHead>
                <TableHead scope="col">What</TableHead>
                <TableHead scope="col">Expires</TableHead>
                <TableHead scope="col" className="text-right">
                  Total
                </TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((r) => {
                const name = r.number ?? r.title;
                return (
                  <TableRow key={r.id} className="group/row" data-quote={r.number ?? r.id}>
                    <TableHead scope="row" className="font-medium">
                      {r.company_name}
                    </TableHead>
                    <TableCell className="tabular-nums">{r.number ?? "—"}</TableCell>
                    <TableCell>{r.title}</TableCell>
                    <TableCell>{formatDate(r.expires_at)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(r.total_minor, r.currency)}
                    </TableCell>
                    <TableCell>
                      <QuoteStatusChip status={r.status} />
                    </TableCell>
                    <TableCell className="w-px">
                      <RowActions>
                        {r.status === "draft" ? (
                          <RowAction
                            icon={SendIcon}
                            label={`Send quote ${name}`}
                            tip="Send"
                            onClick={() => sendQuote.mutate(r)}
                          />
                        ) : null}
                        {r.status === "draft" || r.status === "open" ? (
                          <RowAction
                            icon={XCircleIcon}
                            label={`Cancel quote ${name}`}
                            tip="Cancel"
                            className="hover:text-danger-text"
                            onClick={() => setCancelling(r)}
                          />
                        ) : null}
                        {r.pdf_url ? (
                          <RowAction
                            icon={FileDownIcon}
                            label={`Download quote ${name}`}
                            tip="PDF"
                            disabled={pdf.isPending}
                            onClick={() =>
                              pdf.mutate({
                                reader: "operator",
                                companyId: r.company_id,
                                quoteId: r.id,
                                name,
                              })
                            }
                          />
                        ) : null}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )
      ) : invoices.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ReceiptIcon}
          title={
            filter === "all"
              ? "No invoices yet"
              : `No ${FILTER_LABEL[filter].toLowerCase()} invoices`
          }
        />
      ) : (
        <Table className={DENSE_TABLE} data-slot="op-invoices">
          <TableCaption className="sr-only">Invoices across companies, newest first.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Company</TableHead>
              <TableHead scope="col">Number</TableHead>
              <TableHead scope="col">What</TableHead>
              <TableHead scope="col">Due</TableHead>
              <TableHead scope="col" className="text-right">
                Total
              </TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((r) => {
              const name = r.number ?? `draft for ${r.company_name}`;
              const open = r.display_status === "open" || r.display_status === "overdue";
              return (
                <TableRow key={r.id} className="group/row" data-invoice={r.number ?? r.id}>
                  <TableHead scope="row" className="font-medium">
                    {r.company_name}
                  </TableHead>
                  <TableCell className="tabular-nums">{r.number ?? "—"}</TableCell>
                  <TableCell>{SOURCE_LABEL[r.source]}</TableCell>
                  <TableCell>{r.due_date ? formatDate(r.due_date) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMoney(r.total_minor, r.currency)}
                  </TableCell>
                  <TableCell>
                    <InvoiceStatusChip invoice={r} />
                  </TableCell>
                  <TableCell className="w-px">
                    <RowActions>
                      {r.status === "draft" ? (
                        <RowAction
                          icon={SendIcon}
                          label={`Send ${name}`}
                          tip="Send"
                          onClick={() => send.mutate(r)}
                        />
                      ) : null}
                      {open && r.collection_method === "invoice" ? (
                        <RowAction
                          icon={BellRingIcon}
                          label={`Remind about ${name}`}
                          tip="Remind"
                          onClick={() => remind.mutate(r)}
                        />
                      ) : null}
                      {open || r.status === "uncollectible" ? (
                        <RowAction
                          icon={CircleCheckIcon}
                          label={`Mark ${name} paid`}
                          tip="Mark paid"
                          onClick={() => setPaying(r)}
                        />
                      ) : null}
                      {r.status === "draft" ||
                      r.status === "open" ||
                      r.status === "uncollectible" ? (
                        <RowAction
                          icon={BanIcon}
                          label={`Void ${name}`}
                          tip="Void"
                          className="hover:text-danger-text"
                          onClick={() => setVoiding(r)}
                        />
                      ) : null}
                      {r.pdf_url ? (
                        <RowLinkOut
                          icon={FileDownIcon}
                          label={`PDF of ${name}`}
                          tooltip="PDF ↗"
                          href={r.pdf_url}
                        />
                      ) : null}
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {filter === "quotes" ? null : (
        <LoadMore
          what="invoices"
          hasMore={q.hasNextPage}
          loading={q.isFetchingNextPage}
          onMore={() => void q.fetchNextPage()}
        />
      )}
      {paying ? (
        <MarkPaidDialog api={api} invoice={paying} onClose={() => setPaying(null)} />
      ) : null}
      <AlertDialog open={voiding !== null} onOpenChange={(o) => (o ? null : setVoiding(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void {voiding?.number ?? "this draft"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Nothing is owed on it any more; a milestone on it is pending again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => voiding && voidIt.mutate(voiding)}
            >
              Void invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={cancelling !== null}
        onOpenChange={(o) => (o ? null : setCancelling(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancel quote {cancelling?.number ?? cancelling?.title}?
            </AlertDialogTitle>
            <AlertDialogDescription>The client can no longer accept it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => cancelling && cancelQuote.mutate(cancelling)}
            >
              Cancel quote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function MarkPaidDialog({
  api,
  invoice,
  onClose,
}: {
  api: Api;
  invoice: OperatorInvoiceRow;
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const [date, setDate] = useState(todayIso());
  const [reference, setReference] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.invoices.markPaid", {
        params: { company_id: invoice.company_id, invoice_id: invoice.id },
        body: { paid_on: date, reference: reference.trim() },
      }),
    onSuccess: (r) => {
      toast.add({ title: `${r.number ?? "Invoice"} marked paid`, type: "success" });
      onClose();
    },
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={`Mark ${invoice.number ?? "the invoice"} paid`}
      description={`${formatMoney(invoice.total_minor, invoice.currency)} from ${invoice.company_name}, paid outside Stripe.`}
      pending={save.isPending}
      canSubmit={/^\d{4}-\d{2}-\d{2}$/.test(date) && reference.trim() !== ""}
      submitLabel="Mark paid"
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Paid on"
          type="date"
          max={todayIso()}
          value={date}
          onChange={setDate}
          required
        />
        <TextField
          label="Reference"
          value={reference}
          onChange={setReference}
          maxLength={200}
          placeholder="Wire ref, check no."
          required
        />
      </div>
    </FormDialog>
  );
}

// New invoice or quote ---------------------------------------------------------------------------

type LineDraft = {
  key: number;
  pick: string;
  description: string;
  amount: string;
  qty: string;
  milestone: string;
};

const CUSTOM = "custom";

export function NewDocumentDialog({
  api,
  onClose,
  companyId: fixedCompany,
}: {
  api: Api;
  onClose: () => void;
  /** Opened from a company's row: that company, no picker. */
  companyId?: string;
}) {
  const refresh = useRefresh();
  const companies = useQuery(opCompaniesQuery(api));
  const prices = useQuery(priceListsQuery(api));
  const [companyId, setCompanyId] = useState(fixedCompany ?? "");
  const [kind, setKind] = useState<"invoice" | "quote">("invoice");
  const [title, setTitle] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { key: 1, pick: CUSTOM, description: "", amount: "", qty: "1", milestone: "" },
  ]);
  const [net, setNet] = useState<string | null>(null);
  const [expires, setExpires] = useState(addDaysIso(30));
  const [memo, setMemo] = useState("");
  const company = useQuery({ ...opCompanyQuery(api, companyId), enabled: companyId !== "" });
  const account = company.data?.account;
  const netText = net ?? (account ? String(account.net_terms_days) : "");
  const catalog = prices.data?.catalog ?? [];
  const milestones = (company.data?.projects ?? []).flatMap((p) =>
    p.settled_by_invoice_id
      ? []
      : p.milestones
          .filter((m) => m.status === "pending")
          .map((m) => ({
            ...m,
            projectId: p.project.id,
            label: `${p.project.name} · ${m.name}`,
            currency: p.project.currency,
          })),
  );
  const setLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const parsed = lines.map((l) => ({
    description: l.description.trim(),
    quantity: /^\d+$/.test(l.qty) ? Number(l.qty) : 0,
    unit_amount_minor: parseDollarsToCents(l.amount),
    catalog_key: l.pick === CUSTOM ? null : l.pick,
    milestone_id: l.milestone || null,
  }));
  const linesOk = parsed.every(
    (l) =>
      l.description !== "" &&
      l.quantity >= 1 &&
      l.quantity <= 10_000 &&
      l.unit_amount_minor !== null,
  );
  const total = parsed.reduce((s, l) => s + l.quantity * (l.unit_amount_minor ?? 0), 0);
  const netOk =
    kind === "quote" || account?.collection_method !== "invoice" || /^\d+$/.test(netText);
  // a milestone is billed by its own invoice: alone on an invoice, never on a quote (r10 #12)
  const milestoneLine = lines.find((l) => l.milestone);
  const milestoneOk = !milestoneLine || (kind === "invoice" && lines.length === 1);
  const valid =
    companyId !== "" &&
    linesOk &&
    netOk &&
    milestoneOk &&
    (kind === "invoice" || (title.trim() !== "" && expires >= todayIso()));
  const ids = { company: useId(), memo: useId() };
  // one key per submission: a retry after a lost answer or a pending 502 sends the same key, so
  // the server answers with the first attempt's operation instead of making a second (r10 #11)
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const keyFor = (body: unknown) => {
    const json = JSON.stringify(body);
    if (attempt.current?.body !== json) attempt.current = { body: json, key: newIdempotencyKey() };
    return attempt.current.key;
  };

  const save = useMutation({
    mutationFn: async (send: boolean): Promise<unknown> => {
      const body = parsed.map((l) => ({ ...l, unit_amount_minor: l.unit_amount_minor ?? 0 }));
      if (milestoneLine) {
        const m = milestones.find((x) => x.id === milestoneLine.milestone);
        if (!m) throw new Error("the milestone is no longer pending");
        const params = { company_id: companyId, project_id: m.projectId, milestone_id: m.id };
        return api.client.call("operator.billing.milestones.invoice", {
          params,
          idempotencyKey: keyFor({ params, send }),
          body: { send },
        });
      }
      if (kind === "quote") {
        const quoteBody = {
          title: title.trim(),
          lines: body,
          expires_on: expires,
          memo: memo.trim() || null,
          send,
        };
        return api.client.call("operator.billing.quotes.create", {
          params: { company_id: companyId },
          idempotencyKey: keyFor({ companyId, quoteBody }),
          body: quoteBody,
        });
      }
      const invoiceBody = {
        source: "one_off" as const,
        lines: body,
        memo: memo.trim() || null,
        ...(account?.collection_method === "invoice" ? { days_until_due: Number(netText) } : {}),
        send,
      };
      return api.client.call("operator.billing.invoices.create", {
        params: { company_id: companyId },
        idempotencyKey: keyFor({ companyId, invoiceBody }),
        body: invoiceBody,
      });
    },
    onError: (e) => {
      // a definite refusal made nothing: the next submission is a new one
      if (e instanceof ApiError && [400, 403, 404, 422].includes(e.status)) attempt.current = null;
    },
    onSuccess: (_r, send) => {
      toast.add({
        title: `${kind === "quote" ? "Quote" : "Invoice"} ${send ? "sent" : "saved as a draft"}`,
        type: "success",
      });
      onClose();
    },
    onSettled: refresh,
  });

  return (
    <FormDialog
      open
      wide
      onOpenChange={(o) => (o ? null : onClose())}
      title={kind === "quote" ? "New quote" : "New invoice"}
      pending={save.isPending}
      canSubmit={valid}
      submitLabel="Send"
      onSubmit={() => save.mutate(true)}
      secondary={{ label: "Save draft", onClick: () => save.mutate(false) }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {fixedCompany ? null : (
          <Field>
            <FieldLabel htmlFor={ids.company}>Company</FieldLabel>
            <NativeSelect
              id={ids.company}
              value={companyId}
              required
              onChange={(e) => {
                setCompanyId(e.target.value);
                setNet(null);
                setLines((ls) => ls.map((l) => ({ ...l, milestone: "" })));
              }}
            >
              <NativeSelectOption value="">Choose…</NativeSelectOption>
              {(companies.data?.data ?? []).map((c) => (
                <NativeSelectOption key={c.company_id} value={c.company_id}>
                  {c.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        )}
        <SelectField
          label="Type"
          value={kind}
          options={[
            { value: "invoice", label: "Invoice" },
            { value: "quote", label: "Quote" },
          ]}
          onChange={setKind}
        />
      </div>
      {kind === "quote" ? (
        <TextField label="Title" value={title} onChange={setTitle} maxLength={200} required />
      ) : null}
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0" data-slot="doc-lines">
        <legend className="mb-2 text-sm font-medium">Lines</legend>
        {lines.map((l, i) => (
          <div
            key={l.key}
            className="grid items-end gap-2 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_7rem_4rem_auto]"
            data-line={i + 1}
          >
            <NativeSelect
              aria-label={`Line ${i + 1}: from`}
              value={l.milestone ? `m:${l.milestone}` : l.pick}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith("m:")) {
                  const m = milestones.find((x) => x.id === v.slice(2));
                  if (m)
                    setLine(l.key, {
                      pick: CUSTOM,
                      milestone: m.id,
                      description: m.label,
                      amount: centsToDollarsText(m.amount_minor),
                      qty: "1",
                    });
                  return;
                }
                const item = catalog.find((c) => c.key === v);
                setLine(l.key, {
                  pick: v,
                  milestone: "",
                  ...(item
                    ? {
                        description: item.name,
                        amount: centsToDollarsText(item.default_price_minor),
                      }
                    : {}),
                });
              }}
            >
              <NativeSelectOption value={CUSTOM}>Custom</NativeSelectOption>
              {catalog.map((c) => (
                <NativeSelectOption key={c.id} value={c.key}>
                  {c.name}
                </NativeSelectOption>
              ))}
              {(kind === "invoice" ? milestones : []).map((m) => (
                <NativeSelectOption key={m.id} value={`m:${m.id}`}>
                  Milestone: {m.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Input
              aria-label={`Line ${i + 1}: description`}
              placeholder="Description"
              value={l.description}
              maxLength={500}
              onChange={(e) => setLine(l.key, { description: e.target.value })}
            />
            <Input
              aria-label={`Line ${i + 1}: amount ($)`}
              placeholder="0.00"
              inputMode="decimal"
              value={l.amount}
              aria-invalid={
                l.amount.trim() !== "" && parseDollarsToCents(l.amount) === null ? true : undefined
              }
              onChange={(e) => setLine(l.key, { amount: e.target.value })}
            />
            <Input
              aria-label={`Line ${i + 1}: quantity`}
              inputMode="numeric"
              value={l.qty}
              onChange={(e) => setLine(l.key, { qty: e.target.value })}
            />
            <RowAction
              icon={Trash2Icon}
              label={`Remove line ${i + 1}`}
              tip="Remove line"
              disabled={lines.length === 1}
              onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
            />
          </div>
        ))}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={lines.length >= 50}
            onClick={() =>
              setLines((ls) => [
                ...ls,
                {
                  key: Math.max(...ls.map((x) => x.key)) + 1,
                  pick: CUSTOM,
                  description: "",
                  amount: "",
                  qty: "1",
                  milestone: "",
                },
              ])
            }
          >
            <PlusIcon data-icon="inline-start" />
            Add line
          </Button>
          <span className="text-[13px] tabular-nums" data-field="doc-total">
            Total {formatMoney(total, "USD")}
          </span>
        </div>
        {milestoneOk ? null : (
          <p className="text-[13px] text-destructive" data-slot="milestone-alone">
            A milestone goes on its own invoice.
          </p>
        )}
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-2">
        {kind === "quote" ? (
          <TextField
            label="Expires on"
            type="date"
            min={todayIso()}
            value={expires}
            onChange={setExpires}
            required
          />
        ) : account?.collection_method === "invoice" ? (
          <TextField
            label="Net terms (days)"
            inputMode="numeric"
            value={netText}
            onChange={setNet}
            required
          />
        ) : null}
      </div>
      <Field>
        <FieldLabel htmlFor={ids.memo}>Memo</FieldLabel>
        <Textarea
          id={ids.memo}
          rows={2}
          maxLength={2000}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
